import { formatDateShort } from '~/services/TimeService';

import { pluralizeToString } from '~/utils/stringUtils';

import type { InvalidBlackout, PreviousBlackouts } from '../tabs/blackouts/previousBlackouts';
import type { PreviousEgs } from '../tabs/egs/previousEgs';
import type { PreviousInclusions } from '../tabs/inclusions/previousInclusions';
import { BASIC_CATEGORY_INDEX } from '../tabs/packageOptions/packageCategories';
import type { PreviousDealOptions } from '../tabs/packageOptions/previousDealOptions';
import type { PackageRole, PackageVariantError } from '../tabs/packageOptions/previousPackageVariants';
import type { PreviousSurcharges } from '../tabs/surcharges/previousSurcharges';

const BLACKOUT_NOUN = 'blackout';
const EGS_NOUN = 'extra guest surcharge';
const INCLUSION_NOUN = 'inclusion';
const SURCHARGE_NOUN = 'surcharge period';

export type PreviousOpportunityChoice = {
  id: string;
  name: string;
  stage: string;
  closeDate: string;
};

export type CopySection = 'dealOptions' | 'inclusions' | 'surcharges' | 'extraGuestSurcharges' | 'blackouts';

type CopySectionConfig = {
  key: CopySection;
  label: string;
  replaces: string;
  requires?: { section: CopySection; reason: string };
};

export const COPY_SECTIONS: Array<CopySectionConfig> = [
  { key: 'dealOptions', label: 'Deal Options', replaces: 'Package Options Option 1' },
  {
    key: 'inclusions',
    label: 'Inclusions',
    replaces: 'Option 1 inclusions',
    requires: { section: 'dealOptions', reason: 'Tick Deal Options first: inclusions attach to the rooms it copies.' },
  },
  { key: 'surcharges', label: 'Surcharges', replaces: 'Surcharge periods' },
  { key: 'extraGuestSurcharges', label: 'Extra Guest Surcharges', replaces: 'Extra guest surcharges' },
  { key: 'blackouts', label: 'Blackouts', replaces: 'Blackouts' },
];

export function toggleCopySection(selected: Set<CopySection>, section: CopySection, checked: boolean) {
  const next = new Set(selected);
  if (checked) {
    next.add(section);
    return next;
  }
  next.delete(section);
  COPY_SECTIONS.filter(({ requires }) => requires?.section === section).forEach(({ key }) => next.delete(key));
  return next;
}

export type CopyStep = 'opportunity' | 'sections' | 'packageVariants' | 'surchargeDates' | 'egs' | 'confirm';

export const COPY_STEP_LABELS: Record<CopyStep, string> = {
  opportunity: 'Opportunity',
  sections: 'What to copy',
  packageVariants: 'Package variants',
  surchargeDates: 'Surcharge dates',
  egs: 'Extra guest surcharges',
  confirm: 'Confirm',
};

export type CopyReadiness = {
  hasSource: boolean;
  selected: Set<CopySection>;
  isFetching: boolean;
  dealOptionPackages: number;
  hasPackageVariants: boolean;
  packageVariantErrors: number;
  inclusions: number;
  surchargePeriods: number;
  surchargesToDate: number;
  surchargesToCopy: number;
  everySurchargeDatedOrSkipped: boolean;
  egsRecords: number;
  egsToCopy: number;
  everyEgsHasRoomsOrSkipped: boolean;
  everyEgsOtherBedDescribed: boolean;
  blackouts: number;
};

export function copySteps({
  selected,
  hasPackageVariants,
  surchargesToDate,
}: Pick<CopyReadiness, 'selected' | 'hasPackageVariants' | 'surchargesToDate'>): Array<CopyStep> {
  const steps: Array<CopyStep> = ['opportunity', 'sections'];
  if (selected.has('dealOptions') && hasPackageVariants) {
    steps.push('packageVariants');
  }
  if (selected.has('surcharges') && surchargesToDate > 0) {
    steps.push('surchargeDates');
  }
  if (selected.has('extraGuestSurcharges')) {
    steps.push('egs');
  }
  steps.push('confirm');
  return steps;
}

export function isStepComplete(step: CopyStep, readiness: CopyReadiness): boolean {
  const {
    hasSource,
    selected,
    isFetching,
    dealOptionPackages,
    packageVariantErrors,
    inclusions,
    surchargePeriods,
    surchargesToCopy,
    everySurchargeDatedOrSkipped,
    egsRecords,
    egsToCopy,
    everyEgsHasRoomsOrSkipped,
    everyEgsOtherBedDescribed,
    blackouts,
  } = readiness;
  switch (step) {
    case 'opportunity':
      return hasSource;
    case 'sections':
      return (
        selected.size > 0 &&
        !isFetching &&
        (!selected.has('dealOptions') || dealOptionPackages > 0) &&
        (!selected.has('inclusions') || inclusions > 0) &&
        (!selected.has('surcharges') || surchargePeriods > 0) &&
        (!selected.has('extraGuestSurcharges') || egsRecords > 0) &&
        (!selected.has('blackouts') || blackouts > 0)
      );
    case 'packageVariants':
      return packageVariantErrors === 0 && dealOptionPackages > 0;
    case 'surchargeDates':
      return everySurchargeDatedOrSkipped && surchargesToCopy > 0;
    case 'egs':
      return everyEgsHasRoomsOrSkipped && everyEgsOtherBedDescribed && egsToCopy > 0;
    case 'confirm':
      return true;
  }
}

export function canCopy(readiness: CopyReadiness): boolean {
  return copySteps(readiness).every((step) => isStepComplete(step, readiness));
}

type OpportunitySummary = Pick<App.OpportunityPage, 'id'> & {
  opportunity_info: Pick<App.OpportunityPage['opportunity_info'], 'name' | 'stagename'>;
  sales_and_forecast: Pick<App.OpportunityPage['sales_and_forecast'], 'closedate'>;
};

export function previousOpportunityChoices(
  opportunities: Array<OpportunitySummary>,
  currentOpportunityId: string,
): Array<PreviousOpportunityChoice> {
  return opportunities
    .filter((opportunity) => opportunity.id && opportunity.id !== currentOpportunityId)
    .map((opportunity) => ({
      id: opportunity.id,
      name: opportunity.opportunity_info.name,
      stage: opportunity.opportunity_info.stagename,
      closeDate: opportunity.sales_and_forecast.closedate,
    }))
    .sort((a, b) => (b.closeDate || '').localeCompare(a.closeDate || ''));
}

type CopyNote = { severity: 'info' | 'warning'; text: string };

export type CopySectionDetails = {
  summary?: string;
  emptyMessage?: string;
  emptySeverity?: 'error' | 'info';
  notes: Array<CopyNote>;
  // What Confirm lists under the summary.
  confirmLines?: Array<string>;
};

function presentNotes(notes: Array<[CopyNote['severity'], string | undefined]>): Array<CopyNote> {
  return notes.flatMap(([severity, text]) => (text ? [{ severity, text }] : []));
}

export function dealOptionsNotes(previous: PreviousDealOptions, netCurrency: string): Array<CopyNote> {
  return presentNotes([
    ['info', ambiguousCellsNote(previous)],
    ['warning', mixedOccupancyNote(previous)],
    ['warning', otherCurrencyNetNote(previous, netCurrency)],
  ]);
}

export function egsNotes(previous: PreviousEgs, currency: string): Array<CopyNote> {
  return presentNotes([
    ['warning', egsBeddingNote(previous)],
    ['warning', egsNotCopiedNote(previous, currency)],
  ]);
}

function toCopy(noun: string, count: number): string {
  return `${pluralizeToString(noun, count)} to copy`;
}

export function dealOptionsSummary({ packages }: PreviousDealOptions, lengthsOfStay: Array<number>): string {
  const rooms = packages.filter((previous) => previous.categoryIndex === BASIC_CATEGORY_INDEX).length;
  const copying =
    packages.length > rooms
      ? `${toCopy('package', packages.length)} across ${pluralizeToString('room', rooms)}`
      : toCopy('room', rooms);
  return `${copying} · LOS ${lengthsOfStay.map((nights) => `${nights}N`).join(', ')}`;
}

export const PACKAGE_ROLE_LABELS: Record<PackageRole, string> = {
  base: 'Basic',
  upgrade: 'Upgrade',
  solo: 'Solo traveller',
  soloUpgrade: 'Solo upgrade',
  skip: 'Skip',
};

export function packageVariantErrorText(error: PackageVariantError): string {
  switch (error.reason) {
    case 'noBase':
      return `${error.assetName}: make one package Basic, the others are priced from it.`;
    case 'overlappingStays':
      return error.role === 'base'
        ? `${error.assetName}: two Basic packages both have ${error.nights}N, skip one of them.`
        : `${error.assetName}: two ${PACKAGE_ROLE_LABELS[error.role]} "${error.suffix}" packages both have ${error.nights}N, give one a different suffix or skip it.`;
    case 'noSuffix':
      return `${error.assetName}: add a package suffix for "${error.groupName}".`;
  }
}

function ambiguousCellsNote({ ambiguousCells }: PreviousDealOptions): string | undefined {
  return ambiguousCells
    ? `${pluralizeToString('price', ambiguousCells)} not copied: the previous Opportunity has more than one Deal Option for the same package and stay length. Enter ${ambiguousCells === 1 ? 'it' : 'them'} on Package Options.`
    : undefined;
}

export function mixedOccupancyNote({ mixedOccupancyPackages }: PreviousDealOptions): string | undefined {
  return mixedOccupancyPackages
    ? `Occupancy set to 2A, check it: ${pluralizeToString('package', mixedOccupancyPackages)} whose Deal Options name different occupancies.`
    : undefined;
}

function otherCurrencyNetNote({ otherCurrencyNetCells }: PreviousDealOptions, netCurrency: string): string | undefined {
  return otherCurrencyNetCells
    ? `Net prices not copied for ${pluralizeToString('room and stay length combination', otherCurrencyNetCells)}: their Deal Options cost in another currency than this Opportunity's ${netCurrency}.`
    : undefined;
}

export function inclusionsSummary({ drafts }: PreviousInclusions): string {
  return toCopy(INCLUSION_NOUN, drafts.length);
}

export function inclusionsNotes({ notCopied, otherCurrencyValue }: PreviousInclusions): Array<CopyNote> {
  return presentNotes([
    [
      'info',
      notCopied
        ? `${pluralizeToString(INCLUSION_NOUN, notCopied)} not copied: ${notCopied === 1 ? "it's" : "they're"} only on packages that aren't copied, so there's nothing here to attach ${notCopied === 1 ? 'it' : 'them'} to.`
        : undefined,
    ],
    [
      'warning',
      otherCurrencyValue
        ? `PRV left blank for ${pluralizeToString(INCLUSION_NOUN, otherCurrencyValue)} valued in another currency than AUD. Enter it on the Inclusions tab.`
        : undefined,
    ],
  ]);
}

export function surchargesNotes(previous: PreviousSurcharges): Array<CopyNote> {
  return presentNotes([
    ['warning', surchargesOwnDatesNote(previous)],
    ['info', surchargesToDateNote(previous)],
    ['warning', surchargesNotCopiedNote(previous)],
    ['info', skippedNote(previous)],
  ]);
}

type SkippedCounts = { outsideTravel: number; ended: number; archived?: number };

function skippedReasons({ outsideTravel, ended, archived = 0 }: SkippedCounts): Array<[number, string]> {
  const reasons: Array<[number, string]> = [
    [outsideTravel, "outside this Opportunity's travel dates"],
    [ended, 'already over'],
    [archived, 'archived in Salesforce'],
  ];
  return reasons.filter(([count]) => count > 0);
}

function listOf(items: Array<string>): string {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items.join('');
}

function skippedNote(counts: SkippedCounts) {
  const reasons = skippedReasons(counts).map(([count, reason]) => `${count} ${reason}`);
  return reasons.length ? `Not copied: ${listOf(reasons)}.` : undefined;
}

function nothingAppliesMessage(noun: string, label: string, counts: SkippedCounts): string | undefined {
  const reasons = skippedReasons(counts).map(
    ([count, reason], index) =>
      `${index === 0 ? pluralizeToString(noun, count) : count} ${count === 1 ? 'is' : 'are'} ${reason}`,
  );
  return reasons.length ? `Nothing to copy: ${listOf(reasons)}. Untick ${label} to continue.` : undefined;
}

// Last year's dates missing this Opportunity is the expected case, not an error, so it reads as information.
function emptyDetails(
  nothingApplies: string | undefined,
  noneMessage: string,
  notes: Array<CopyNote>,
): CopySectionDetails {
  return nothingApplies
    ? { emptyMessage: nothingApplies, emptySeverity: 'info', notes }
    : { emptyMessage: noneMessage, notes };
}

export function surchargesEmptyDetails(previous: PreviousSurcharges): CopySectionDetails {
  return emptyDetails(
    nothingAppliesMessage(SURCHARGE_NOUN, 'Surcharges', previous),
    'That Opportunity has no surcharge periods that can be copied.',
    presentNotes([['warning', surchargesNotCopiedNote(previous)]]),
  );
}

export function surchargesSummary(copied: number, skipped = 0): string {
  return `${toCopy(SURCHARGE_NOUN, copied)}${skipped ? ` (${skipped} skipped, no dates)` : ''}`;
}

function surchargesOwnDatesNote({ periods }: PreviousSurcharges): string | undefined {
  return periods.length
    ? "Dates are copied as they are. Check them against this Opportunity's travel dates."
    : undefined;
}

function surchargesToDateNote({ toDate }: PreviousSurcharges): string | undefined {
  return toDate.length
    ? `${pluralizeToString(SURCHARGE_NOUN, toDate.length)} with missing dates in Salesforce: set them in the next step.`
    : undefined;
}

function surchargesNotCopiedNote({ noSell, paidToHotel }: PreviousSurcharges): string | undefined {
  const reasons = [
    noSell && `${noSell} with no sell price`,
    paidToHotel && `${paidToHotel} paid directly to the hotel`,
  ].filter(Boolean);
  return reasons.length ? `Not copied, add them yourself: ${reasons.join(', ')}.` : undefined;
}

export function surchargesOverlapWarning(overlappingNotAccumulating: number): string | undefined {
  return overlappingNotAccumulating
    ? `Check ${pluralizeToString(SURCHARGE_NOUN, overlappingNotAccumulating)} that overlap another but don't accumulate in Salesforce: the tab adds overlapping periods together.`
    : undefined;
}

export function egsSummary(copied: number, skipped = 0, withoutBed = 0): string {
  const details = [
    skipped && `${skipped} skipped`,
    withoutBed && `${withoutBed} ${withoutBed === 1 ? 'needs' : 'need'} an extra bed on the EGS tab`,
  ].filter(Boolean);
  return `${toCopy(EGS_NOUN, copied)}${details.length ? ` (${details.join(', ')})` : ''}`;
}

function egsBeddingNote({ beddingToChoose }: PreviousEgs): string | undefined {
  return beddingToChoose
    ? `Salesforce lists the extra bed as "Roll-away, sofa bed or daybed" for ${pluralizeToString(EGS_NOUN, beddingToChoose)}, which the tab splits into separate options. Pick it on the next step.`
    : undefined;
}

function egsNotCopiedNote(
  { paidToHotel, otherCollectedBy, otherGuestType, otherCurrency }: PreviousEgs,
  currency: string,
): string | undefined {
  const reasons = [
    paidToHotel && `${paidToHotel} paid directly to the hotel`,
    otherCollectedBy && `${otherCollectedBy} with an empty or unknown collected by`,
    otherGuestType && `${otherGuestType} for a guest type the EGS tab has no category for`,
    otherCurrency && `${otherCurrency} with a cost in another currency than this Opportunity's ${currency}`,
  ].filter(Boolean);
  return reasons.length ? `Not copied, add them yourself: ${reasons.join(', ')}.` : undefined;
}

function invalidBlackoutNote({ name, startDate, endDate }: InvalidBlackout): string {
  if (startDate && endDate) {
    return `The "${name}" blackout wasn't copied: in Salesforce it ends before it starts (${formatDateShort(startDate)} to ${formatDateShort(endDate)}). Add it on the Blackouts tab with the right dates.`;
  }
  const missing = startDate ? 'end date' : endDate ? 'start date' : 'dates';
  return `The "${name}" blackout wasn't copied: it has no ${missing} in Salesforce. Add it on the Blackouts tab.`;
}

export function blackoutsDetails(previous: PreviousBlackouts): CopySectionDetails {
  const invalidNote: Array<CopyNote> = previous.invalid.map((blackout) => ({
    severity: 'warning',
    text: invalidBlackoutNote(blackout),
  }));
  if (!previous.blackouts.length) {
    return emptyDetails(
      nothingAppliesMessage(BLACKOUT_NOUN, 'Blackouts', previous),
      'That Opportunity has no blackouts that can be copied.',
      invalidNote,
    );
  }
  return {
    summary: toCopy(BLACKOUT_NOUN, previous.blackouts.length),
    notes: [...presentNotes([['info', skippedNote(previous)]]), ...invalidNote],
  };
}

export function replaceWarning(selected: Set<CopySection>): string | undefined {
  return replacesWarning(
    COPY_SECTIONS.filter((section) => selected.has(section.key)).map((section) => section.replaces),
  );
}

export function replacesWarning(replaced: ReadonlyArray<string>): string | undefined {
  return replaced.length
    ? `This replaces the current ${replaced.join(', ')}. Anything already typed there will be lost.`
    : undefined;
}
